// src/pages/FirmsPage.js (Versión Completa y Funcional con Carga Masiva)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Link, 
  CircularProgress, 
  Alert, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogContentText, 
  DialogActions 
} from '@mui/material';
import FirmsTable from '../components/FirmsTable';
import AddFirmDialog from '../components/AddFirmDialog';
import { 
  getFirms, 
  createFirm, 
  updateFirm, 
  deleteFirm, 
  uploadFirmsCSV 
} from '../services/api';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useNotification } from '../context/NotificationContext';

const FirmsPage = () => {
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFirm, setEditingFirm] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [firmToDelete, setFirmToDelete] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { showNotification } = useNotification();

  const fetchFirms = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getFirms();
      setFirms(response.data);
    } catch (err) {
      setError(err.message || 'Could not fetch firms');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFirms();
  }, [fetchFirms]);

  const handleOpenAddDialog = () => {
    setEditingFirm(null);
    setDialogOpen(true);
  };

  const handleOpenEditDialog = (firm) => {
    setEditingFirm(firm);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingFirm(null);
  };

  const handleSave = async (newData) => {
    const isEditing = !!editingFirm;
    try {
      if (isEditing) {
        await updateFirm(editingFirm.firm_id, newData);
      } else {
        await createFirm(newData);
      }
      fetchFirms();
      showNotification(`Firm ${isEditing ? 'updated' : 'created'} successfully!`, 'success');
    } catch (err) {
      const errorMessage = err.response?.data?.errors?.[0]?.msg || 'Failed to save the firm.';
      showNotification(errorMessage, 'error');
    }
  };
  
  const handleOpenConfirmDelete = (firm) => {
    setFirmToDelete(firm);
    setConfirmDeleteOpen(true);
  };

  const handleCloseConfirmDelete = () => {
    setFirmToDelete(null);
    setConfirmDeleteOpen(false);
  };
  
  const handleDelete = async () => {
    if (!firmToDelete) return;
    try {
      await deleteFirm(firmToDelete.firm_id);
      fetchFirms();
      showNotification('Firm deleted successfully!', 'success');
    } catch (err) {
      showNotification('Failed to delete the firm.', 'error');
    } finally {
      handleCloseConfirmDelete();
    }
  };

  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadFirmsCSV(file);
      const { successCount, errorCount, errors } = response.data;
      let message = `${successCount} firms imported successfully.`;
      if (errorCount > 0) {
        message += ` ${errorCount} rows failed.`;
        console.error("CSV Import Errors:", errors);
      }
      showNotification(message, 'success');
      fetchFirms();
    } catch (err) {
      showNotification('File upload failed. Please check the file format.', 'error');
    } finally {
      setUploading(false);
      event.target.value = null;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          Manage Firms
        </Typography>
        <Box>
          <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={handleImportClick} disabled={uploading} sx={{ mr: 1 }}>
            {uploading ? <CircularProgress size={24} /> : 'Import from CSV'}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddDialog}>
            Add New Firm
          </Button>
        </Box>
      </Box>
      
      <Alert severity="info" sx={{ mb: 2 }}>
        To import data, please use a CSV file with columns: 
        <strong> firm_name (optional), address_key, route</strong>. 
        <Link href="/template_firms.csv" download sx={{ ml: 1, fontWeight: 'bold' }}>
          Download Template
        </Link>
      </Alert>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept=".csv"
      />

      <FirmsTable 
        firms={firms} 
        loading={loading} 
        error={error} 
        onEditClick={handleOpenEditDialog} 
        onDeleteClick={handleOpenConfirmDelete}
      />
      <AddFirmDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSubmit={handleSave}
        initialData={editingFirm}
      />
      <Dialog open={confirmDeleteOpen} onClose={handleCloseConfirmDelete}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the firm for "{firmToDelete?.address_key}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDelete}>Cancel</Button>
          <Button onClick={handleDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FirmsPage;
